import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, User, Headphones } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVATAR = 'https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/ujn9vuw4_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20260306102346_149_131%281%29.jpg';

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hi! I\'m ALIGCUS Architectural Consultant. How can I help you today?\n\n您好！我是ALIGCUS建筑顾问，有什么可以帮您？' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `s-${Date.now()}`);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for admin replies
  useEffect(() => {
    if (!open) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/chatbot/messages/${sessionId}`);
        const serverMsgs = r.data.messages || [];
        if (serverMsgs.length > messages.length) {
          setMessages(serverMsgs);
        }
      } catch {}
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [open, sessionId, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const newMsgs = [...messages, { role: 'user', text }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chatbot/ask`, { message: text, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'bot', text: r.data.response }]);
      // Log chat to access_logs
      axios.post(`${API}/admin/access-log`, {
        ip: 'client', chat_session_id: sessionId, chat_name: 'Alice (Bot)',
        chat_message: text, click_event: 'chat_message',
        login_email: localStorage.getItem('admin_email') || '',
        login_role: localStorage.getItem('admin_role') || '',
      }).catch(() => {});
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Sorry, please try again or call 626-803-8000.' }]);
    }
    setLoading(false);
  };

  const BotAvatar = () => (
    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
      <img src={AVATAR} alt="Alice" className="w-full h-full object-cover" />
    </div>
  );

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 z-[60] overflow-hidden"
          style={{ animation: 'pulse-glow 2s infinite' }}>
          <img src={AVATAR} alt="Alice" className="w-full h-full object-cover" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-24 right-6 w-[380px] max-w-[calc(100vw-48px)] h-[520px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-[60] border border-gray-200">
          <div className="bg-red-600 text-white px-5 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                <img src={AVATAR} alt="Alice" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">Alice</p>
                <p className="text-xs text-red-200">Online</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-full p-1 transition-colors"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.role === 'user' ? (
                    <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0"><User className="w-3.5 h-3.5 text-white" /></div>
                  ) : (
                    <BotAvatar />
                  )}
                  <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${msg.role === 'user' ? 'bg-gray-800 text-white rounded-tr-sm' : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-tl-sm'}`}>
                    {msg.text}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex gap-2">
                  <BotAvatar />
                  <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm border border-gray-100">
                    <div className="flex gap-1"><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} /><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} /><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} /></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 bg-white border-t flex-shrink-0">
            <div className="flex gap-2">
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Ask a question... / 输入问题..."
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-red-400 focus:bg-white transition-colors" />
              <button onClick={send} disabled={loading || !input.trim()}
                className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 disabled:opacity-40 transition-all flex-shrink-0">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(220, 38, 38, 0); }
        }
      `}</style>
    </>
  );
};

export default Chatbot;
